<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchAsset,
  createAsset,
  updateAsset,
  fetchAssetPlugins,
} from '@apis/admin/asset'
import LangItem from '@components/admin/lang-input'
import FormEditor from '@components/admin/form-editor'
import AssetWithdrawFee from '@components/admin/asset-withdraw-fee'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { LangItem, FormEditor, AssetWithdrawFee },
  props: {
    asset_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      loading: false,
      active_tab: 'info',
      asset_plugins: [],
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadAsset(), this.loadAssetPlugins()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAsset() {
      const { asset_info } = await fetchAsset(this.asset_seq)
      this.params = defaults(asset_info, {
        title: '',
        code: '',
        is_active: 'N',
        allow_deposit: 'N',
        allow_withdraw: 'N',
        use_withdraw_fee: 'N',
        decimal_point: 0,
        plugin_seq: 0,
        list_order: 0,
        seq: null,
        regdate: null,
      })
      this.info = asset_info
    },
    async loadAssetPlugins() {
      const { data } = await fetchAssetPlugins()

      this.asset_plugins = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateAsset : createAsset
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_asset_modify')
        : this.$t('admin.msg_asset_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.plugin') }}</div></th
              >
              <td>
                <select
                  v-model="params.plugin_seq"
                  :disabled="Boolean(info && info.plugin_seq)"
                >
                  <option :value="0">{{ $t('admin.plugin_select') }}</option>
                  <option
                    v-for="{ seq, title } in asset_plugins"
                    :key="seq"
                    :value="seq"
                    >{{ title }}</option
                  >
                </select>
              </td>
              <th scope="row"
                ><div>{{ $t('admin.c2c_market_regdate') }}</div></th
              >
              <td>{{
                params.seq
                  ? date('Y-m-d H:i:s', new Date(params.regdate))
                  : $t('admin.before_enroll')
              }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.asset_title') }}</div></th
              >
              <td colspan="3"
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.form_info_list_order') }}</div></th
              >
              <td
                ><input
                  v-model="params.list_order"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              /></td>
              <th scope="row"
                ><div>{{ $t('admin.modal_asset_code') }}</div></th
              >
              <td
                ><input
                  v-model="params.code"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40]"
              /></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.asset_decimal') }}</div></th
              >
              <td
                ><input
                  v-model="params.decimal_point"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              /></td>
              <th scope="row"
                ><div>{{ $t('admin.modal_is_active') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.is_active"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.allow_deposit') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.allow_deposit"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
              <th scope="row"
                ><div>{{ $t('admin.allow_withdraw') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.allow_withdraw"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('asset_info.withdrawal_fee_usage') }}</div></th
              >
              <td colspan="3">
                <ElSwitch
                  v-model="params.use_withdraw_fee"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch>
                <template v-if="params.use_withdraw_fee === 'Y'">
                  {{ $t('asset_info.msg_withdrawal_fee_done') }}
                </template>
              </td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!params.seq"
      :label="$t('asset_info.withdrawal_fee')"
      name="ASSET_WITHDRAW_FEE"
    >
      <AssetWithdrawFee :asset_seq="params.seq"></AssetWithdrawFee>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!params.seq"
      :label="$t('admin.deposit_form')"
      name="ASSET_DEPOSIT"
    >
      <FormEditor
        v-if="params.seq"
        target_type="ASSET_DEPOSIT"
        :target_seq="params.seq"
      ></FormEditor>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!params.seq"
      :label="$t('admin.withdraw_form')"
      name="ASSET_WITHDRAW"
    >
      <FormEditor
        v-if="params.seq"
        target_type="ASSET_WITHDRAW"
        :target_seq="params.seq"
      ></FormEditor>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
